import { parseLocalDate } from './revenueTrends';

/**
 * Fechas NUMÉRICAS, siempre en formato de Estados Unidos: M/D/AAAA.
 *
 * ── Por qué fijo y no por idioma ────────────────────────────────────────
 *
 * El taller trabaja en Estados Unidos y sus clientes leen fechas a la
 * americana, hable español o inglés quien mire la pantalla. Atar el formato al
 * selector de idioma haría que la MISMA factura dijera 9/16/2026 para la
 * oficina y 16/9/2026 para la cuadrilla — y «6/8/2026» no se puede desambiguar
 * mirándolo: puede ser el 6 de agosto o el 8 de junio.
 *
 * Esto vale para la fecha en CIFRAS. Los formatos con el mes en LETRA
 * («agosto 2026», «lun, 11 ago») sí siguen al idioma, porque ahí el mes es una
 * palabra que hay que traducir, no una convención numérica que confunde.
 *
 * ── El segundo fallo, más grave que el formato ──────────────────────────
 *
 * Casi todas estas llamadas construían un Date desde la cadena guardada y lo
 * formateaban, y eso enseñaba EL DÍA ANTERIOR. Con una cadena de solo fecha,
 * `new Date('2026-08-01')` es medianoche UTC, que en Las Vegas cae el 31 de
 * julio por la tarde — así que la pantalla decía 7/31/2026.
 *
 * No era un problema de formato: la app enseñaba una fecha equivocada.
 * `parseLocalDate` ya resuelve eso —lee las cadenas de solo fecha como día
 * LOCAL y las marcas de tiempo completas como el instante que son—, así que
 * todo pasa por ahí.
 */

/** El formato de fecha de Estados Unidos. No se toca con el idioma. */
export const LOCALE_FECHA = 'en-US';

/**
 * Fecha en cifras: `8/1/2026`. Cadena vacía si no hay fecha utilizable, para
 * que una ficha sin dato enseñe un hueco y no «Invalid Date».
 */
export function fechaCorta(valor?: string | Date | null): string {
    const d = valor instanceof Date ? valor : parseLocalDate(valor ?? null);
    if (!d || Number.isNaN(d.getTime())) return '';
    return d.toLocaleDateString(LOCALE_FECHA);
}

/**
 * HOY en cifras: `8/18/2026`.
 *
 * Existe porque `fechaCorta()` SIN argumento devuelve cadena vacía. Eso es lo
 * correcto para un dato que falta —una ficha sin fecha enseña un hueco, no
 * «Invalid Date»—, pero quien quería «hoy» se llevaba el hueco en silencio.
 *
 * Pasó, y dejó la firma de contratos rota: el campo de fecha del flujo de firma
 * se autollenaba con `fechaCorta()`, guardaba '', se leía como sin llenar, y el
 * botón «Firmar contrato» no se habilitaba NUNCA — con la firma dibujada, el
 * nombre escrito y la casilla marcada. Un campo de fecha obligatorio no se
 * podía llenar ni a mano (b55edfa, 17-ago-2026).
 *
 * Si quieres el día de hoy, pídelo aquí. `fechaCorta()` a pelo no lo da.
 */
export function hoyCorto(): string {
    return fechaCorta(new Date());
}

/** Fecha y hora en cifras: `8/1/2026, 3:04 PM`. Para sellos de auditoría. */
export function fechaHora(valor?: string | Date | null): string {
    const d = valor instanceof Date ? valor : parseLocalDate(valor ?? null);
    if (!d || Number.isNaN(d.getTime())) return '';
    return d.toLocaleString(LOCALE_FECHA);
}

/**
 * ── Un DÍA no es un instante ────────────────────────────────────────────
 *
 * `fechaCorta` resuelve la cadena de SOLO fecha (`'2026-09-01'`), pero en la
 * base de datos casi ningún día vive así: las columnas son `timestamptz` y el
 * día se guarda como `'2026-09-01T00:00:00+00:00'` — medianoche UTC. Esa cadena
 * ya no la salva el rescate de `parseLocalDate` (su patrón exige la cadena
 * entera y ésta arrastra hora), así que cae en `new Date(instante)` y en Las
 * Vegas vuelve a enseñar el 31 de agosto por la tarde. El mismo fallo de
 * siempre, entrando por la otra puerta.
 *
 * Se midió en producción el 22-ago-2026: 18 de 33 visitas de servicio, 17 de 69
 * fechas de inicio de proyecto y las 4 de 4 de próximo servicio de equipo están
 * a `00:00:00+00`.
 *
 * `diaLocal` lee los diez primeros caracteres —el día en cifras, tal como se
 * escribió— y arma con ellos un Date LOCAL. La hora se tira a propósito: quien
 * apuntó «1 de septiembre» apuntó un día del calendario, no un instante, y ese
 * día es el 1 de septiembre se mire desde donde se mire.
 *
 * Por eso NO sirve para sellos de auditoría ni para nada que sea de verdad un
 * momento en el tiempo — para eso están `fechaHora` y `fechaCorta`, que se
 * quedan como estaban.
 *
 * OJO con el argumento `Date`: se devuelve TAL CUAL, sin normalizar, porque un
 * `Date` ya se construyó en algún huso y aquí no hay forma de saber cuál. Las
 * dos puertas no coinciden a propósito:
 *   diaLocal('2026-09-01T00:00:00Z')            -> 1 de septiembre
 *   diaLocal(new Date('2026-09-01T00:00:00Z'))  -> 31 de agosto en Las Vegas
 * Si tienes la cadena, pásale la cadena.
 */
export function diaLocal(valor?: string | Date | null): Date | null {
    if (!valor) return null;
    if (valor instanceof Date) return Number.isNaN(valor.getTime()) ? null : valor;
    const dia = /^(\d{4})-(\d{2})-(\d{2})/.exec(valor);
    if (!dia) {
        // Formato raro (ni ISO ni nada que empiece por AAAA-MM-DD): que lo
        // intente quien ya lo intentaba, para no empeorar lo que hoy funciona.
        return parseLocalDate(valor);
    }
    const d = new Date(Number(dia[1]), Number(dia[2]) - 1, Number(dia[3]));
    return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * Día del calendario en cifras: `9/1/2026`, venga como `'2026-09-01'` o como
 * `'2026-09-01T00:00:00+00:00'`. Cadena vacía si no hay fecha utilizable.
 */
export function fechaDeDia(valor?: string | Date | null): string {
    const d = diaLocal(valor);
    if (!d || Number.isNaN(d.getTime())) return '';
    return d.toLocaleDateString(LOCALE_FECHA);
}
